import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react'

import { FieldError } from 'react-hook-form'

import clsx from 'clsx'

import styles from './styles.module.scss'

export interface InputWithLabelProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  error?: FieldError
  variant?: 'default' | 'secondary'
  containerClassName?: string
}

export const InputWithLabel = forwardRef<HTMLInputElement, InputWithLabelProps>(
  (props, ref) => {
    const {
      label,
      leftIcon,
      rightIcon,
      error,
      className,
      containerClassName,
      id,
      variant = 'default',
      placeholder = ' ',
      ...rest
    } = props

    const reactId = useId()
    const inputId = id || `input-${reactId}`

    return (
      <div className={styles.wrapper}>
        <div
          className={clsx(
            styles.container,
            styles[`variant-${variant}`],
            error && styles.error,
            leftIcon && styles.hasLeftIcon,
            containerClassName,
          )}
        >
          {leftIcon && <div className={styles.leftIcon}>{leftIcon}</div>}

          <input
            ref={ref}
            id={inputId}
            className={clsx(
              styles.input,
              leftIcon && styles.withLeftIcon,
              rightIcon && styles.withRightIcon,
              className,
            )}
            placeholder={placeholder}
            aria-invalid={!!error}
            {...rest}
          />

          <label
            htmlFor={inputId}
            className={styles.label}
          >
            {label}
          </label>

          {rightIcon && <div className={styles.rightIcon}>{rightIcon}</div>}
        </div>

        {error?.message && <p className={styles.errorText}>{error?.message}</p>}
      </div>
    )
  },
)

InputWithLabel.displayName = 'InputWithLabel'
